/**
 * Status vocabulary for booked work. Tokens live apart from the components
 * that render them (same split as the mobile list rows) so the row shell
 * stays status-free and other surfaces can bring their own token maps.
 */

/** The statuses the portal has a word and a colour for. */
export type WorkStatus =
  | 'InProgress'
  | 'Booked'
  | 'Approved'
  | 'Rejected'
  | 'Cancelled';

/** A status rendered as an 8px dot plus a word. Colour is never the only channel. */
export interface StatusToken {
  word: string;
  /** Theme palette path (e.g. 'success.main'), usable in sx color/bgcolor. */
  color: string;
}

/**
 * `Booked` reads as "Pending" — what the portal already shows on allocation
 * rows, and the wording Activity unifies onto during rollout.
 */
export const workStatusTokens: Record<WorkStatus, StatusToken> = {
  InProgress: { word: 'In progress', color: 'info.main' },
  Booked: { word: 'Pending', color: 'warning.main' },
  Approved: { word: 'Approved', color: 'success.main' },
  Rejected: { word: 'Rejected', color: 'error.main' },
  Cancelled: { word: 'Cancelled', color: 'text.disabled' },
};

const tokensByStatus = new Map<string, StatusToken>(
  Object.entries(workStatusTokens)
);

/** "PartiallyApproved" reads as "Partially approved". */
function humaniseStatus(status: string): string {
  const spaced = status.replace(/([a-z0-9])([A-Z])/g, '$1 $2').trim();
  if (spaced.length === 0) {
    return status;
  }
  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();
}

/**
 * A token for any status string. Statuses reach the portal as bare strings
 * (`Status.ToString()` server-side), so one outside the union has to degrade
 * to a neutral dot and its own name rather than take the row tree down.
 */
export function statusToken(status: string): StatusToken {
  const known = tokensByStatus.get(status);
  if (known) {
    return known;
  }
  return { word: humaniseStatus(status), color: 'text.secondary' };
}

export function statusWord(status: string): string {
  return statusToken(status).word;
}

/** Cancelled rows dim and strike their amount instead of carrying a tag. */
export function isCancelledStatus(status: string | undefined): boolean {
  return status === 'Cancelled';
}

/** Admin cancel is offered on pending and rejected work only. */
export function canCancelWork(status: string | undefined): boolean {
  return status === 'Booked' || status === 'Rejected';
}

/**
 * Accessible name for a row: the status word is dot-only on screen above the
 * collapse point, so it has to reach screen readers some other way.
 */
export function composeRowAriaLabel(
  title: string,
  status: string | undefined,
  extraParts: readonly (string | null | undefined)[] = []
): string {
  const parts = [
    title,
    status != null ? statusWord(status) : null,
    ...extraParts,
  ];
  return parts
    .filter((part): part is string => part != null && part.length > 0)
    .join(', ');
}
